import { NOTIFICATION_DIALOG_ACTIONS } from '@src/app/notifications/dialog';
import { useNotifilcationDialog } from '@src/app/notifications/dialog/hooks';
import { useFormContext } from '@src/shared/form';
import {
  formatDateRange,
  formatIsoDate,
  getServerErrorMessage,
} from '@src/shared/lib';
import { Button, SubmitButton } from '@src/shared/ui/Button';
import { Form, FormControlLabel } from '@src/shared/ui/form';
import { Switch } from '@src/shared/ui/inputs';
import { Stack } from '@src/shared/ui/layout';
import Typography from '@src/shared/ui/Typography';
import { createSchema, Yup } from '@src/shared/validation';
import { useEffect, useMemo, useState } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { getAbsenceBlock, requiresChangeRequest } from '../../lib';
import { useTimeline } from '../../model/hooks';
import { useAbsenceTypes } from '../../model/query';
import type { Absence, AbsenceType } from '../../types';
import {
  ABSENCE_LOCK_STATUS,
  isAbsenceLockErrorStatus,
  isAbsenceLockHeldStatus,
  type SlotData,
  type TimelineSlot,
} from '../../types';
import FormFieldset from './Fields';
import FormHeader from './FormHeader';

type OverrideAbsenceDetail = {
  key: string;
  typeName: string;
  from: Date;
  to: Date;
  startTime: string | null;
  endTime: string | null;
};

function hasRangeChanged(
  editedAbsences: Absence[],
  payload: { dateFrom: unknown; dateTo: unknown },
): boolean {
  if (!editedAbsences.length) return false;
  const dates = editedAbsences.map((a) => a.dateStr).sort();
  return (
    formatIsoDate(payload.dateFrom as Date) !== dates[0] ||
    formatIsoDate(payload.dateTo as Date) !== dates[dates.length - 1]
  );
}

function getOverrideAbsenceDetails(
  selectedIds: number[],
  absences: Absence[],
  absenceTypes: AbsenceType[],
): OverrideAbsenceDetail[] {
  const selectedAbsences = absences.filter((absence) =>
    selectedIds.includes(absence.id),
  );

  const groupedAbsences = new Map<string, OverrideAbsenceDetail>();
  const hourlyAbsences: OverrideAbsenceDetail[] = [];

  selectedAbsences.forEach((absence) => {
    const typeName =
      absence.typeId != null
        ? absenceTypes.find((type) => type.id === absence.typeId)?.name
        : undefined;
    const resolvedTypeName = typeName ?? 'Unknown';
    const isHourly = !!absence.startTime && !!absence.endTime;

    if (isHourly) {
      hourlyAbsences.push({
        key: `absence-${absence.id}`,
        typeName: resolvedTypeName,
        from: absence.date,
        to: absence.date,
        startTime: absence.startTime,
        endTime: absence.endTime,
      });
      return;
    }

    const groupKey =
      absence.typeId != null
        ? `type-${absence.typeId}`
        : `absence-${absence.id}`;
    const existingGroup = groupedAbsences.get(groupKey);

    if (!existingGroup) {
      groupedAbsences.set(groupKey, {
        key: groupKey,
        typeName: resolvedTypeName,
        from: absence.date,
        to: absence.date,
        startTime: null,
        endTime: null,
      });
      return;
    }

    if (absence.date < existingGroup.from) {
      existingGroup.from = absence.date;
    }

    if (absence.date > existingGroup.to) {
      existingGroup.to = absence.date;
    }
  });

  return [...groupedAbsences.values(), ...hourlyAbsences].sort(
    (a, b) => a.from.getTime() - b.from.getTime(),
  );
}

export type WithdrawScope = {
  /** Granularity of the absence being withdrawn; fixed for the whole flow. */
  hourly: boolean;
  minDate: Date;
  maxDate: Date;
  minTime: string | null;
  maxTime: string | null;
};

export type ScheduleFormValues = {
  userId: number | null;
  dateFrom: Date | null;
  dateTo?: Date | null;
  startTime?: string | null;
  endTime?: string | null;
  typeId: number | null;
  tentative?: boolean;
  notes?: string;
};

function getDefaultValues(slotData: SlotData | null): ScheduleFormValues {
  const values: ScheduleFormValues = {
    userId: null,
    dateFrom: null,
    dateTo: null,
    startTime: null,
    endTime: null,
    typeId: null,
    tentative: false,
    notes: '',
  };
  if (!slotData) return values;

  const userId = slotData.userId;
  if (userId) values.userId = userId;

  const dateFrom = slotData.start;
  if (dateFrom) values.dateFrom = new Date(dateFrom);

  const dateTo = slotData.end;
  if (dateTo) values.dateTo = new Date(dateTo);

  values.startTime = slotData.startTime ?? null;
  values.endTime = slotData.endTime ?? null;
  values.typeId = slotData.typeId;
  values.tentative = slotData.tentative;
  values.notes = slotData.notes;

  return values;
}

/**
 * Bounds for withdrawal: an absence can only be given back within the range it
 * already occupies, and its granularity is fixed — an all-day absence cannot be
 * turned into an hourly one on the way out. Returns null outside withdraw mode.
 *
 * The bounds span the whole contiguous block the selection belongs to, not just
 * the days that happened to be selected, so someone who clicked a single day of
 * a multi-day absence can still widen the withdrawal to its neighbours.
 */
function useWithdrawScope(
  active: boolean,
  slot: TimelineSlot | null,
  absences: Absence[],
): WithdrawScope | null {
  return useMemo(() => {
    if (!active) return null;

    const ids =
      slot?.editedAbsenceIds ??
      (slot?.editedAbsenceId ? [slot.editedAbsenceId] : []);
    const selected = absences
      .filter((absence) => ids.includes(absence.id))
      .sort((left, right) => left.dateStr.localeCompare(right.dateStr));

    if (!selected.length) return null;

    const anchor = selected[0];
    // Hourly absences are never part of an all-day block, so they stay bound to
    // themselves and a single-day block collapses to the same thing.
    const block = anchor.allDay ? getAbsenceBlock(anchor, absences) : [];
    const range = block.length ? block : selected;
    const first = range[0];
    const last = range[range.length - 1];

    return {
      hourly: !anchor.allDay,
      minDate: new Date(`${first.dateStr}T00:00:00`),
      maxDate: new Date(`${last.dateStr}T00:00:00`),
      minTime: anchor.startTime,
      maxTime: anchor.endTime,
    };
  }, [active, slot, absences]);
}

function TentativeSwitch({ disabled }: { disabled: boolean }) {
  const { control } = useFormContext() as UseFormReturn<ScheduleFormValues>;
  return (
    <Controller
      name="tentative"
      control={control}
      render={({ field }) => (
        <Switch
          disabled={disabled}
          checked={Boolean(field.value)}
          onBlur={field.onBlur}
          onChange={(_, checked) => field.onChange(checked)}
          slotProps={{ input: { ref: field.ref } }}
        />
      )}
    />
  );
}

function ScheduleFormActions({
  disabled,
  withdrawing,
}: {
  disabled: boolean;
  withdrawing: boolean;
}) {
  const { absenceTypes } = useAbsenceTypes();
  const { selectedSlot, withdraw } = useTimeline();
  const { setValue, watch } =
    useFormContext() as UseFormReturn<ScheduleFormValues>;
  const [typeId, tentative] = watch(['typeId', 'tentative']);
  const selectedType = absenceTypes.find((type) => type.id === typeId);
  const canSaveTentative = !!selectedType?.tentativeable && !withdrawing;
  const [isWithdrawPending, setIsWithdrawPending] = useState(false);

  useEffect(() => {
    if (!canSaveTentative && tentative) {
      setValue('tentative', false);
    }
  }, [canSaveTentative, setValue, tentative]);

  if (withdrawing) {
    // Whatever the narrowed range currently covers, as kept up to date by the
    // fieldset's round-trip effect.
    const withdrawIds = selectedSlot?.selectedIds ?? [];

    const onWithdraw = async () => {
      if (!withdrawIds.length || isWithdrawPending) {
        return;
      }
      setIsWithdrawPending(true);
      try {
        await withdraw(withdrawIds);
      } finally {
        setIsWithdrawPending(false);
      }
    };

    return (
      <Stack spacing={1.25}>
        <Button
          disabled={disabled || !withdrawIds.length || isWithdrawPending}
          variant="contained"
          type="button"
          onClick={onWithdraw}
        >
          <Typography variant="body1">Withdraw Absence</Typography>
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.25}>
      {canSaveTentative && (
        <FormControlLabel
          control={<TentativeSwitch disabled={disabled} />}
          label={<Typography variant="body2">Tentative</Typography>}
        />
      )}
      <SubmitButton
        disabled={disabled}
        variant="contained"
        loadingPosition="start"
      >
        <Typography variant="body1">Request</Typography>
      </SubmitButton>
    </Stack>
  );
}

export default function ScheduleForm() {
  const {
    absenceLock,
    absenceEditor,
    selectedSlot,
    selectedSlotData,
    book,
    isDayView,
    absences,
    sidebarMode,
  } = useTimeline();
  const { absenceTypes } = useAbsenceTypes();
  const withdrawScope = useWithdrawScope(
    sidebarMode === 'withdraw',
    selectedSlot,
    absences,
  );

  const schema = useMemo(
    () =>
      createSchema({
        userId: Yup.number().required('This field is required'),
        dateFrom: Yup.date().required('This field is required'),
        dateTo: isDayView
          ? Yup.date().nullable()
          : Yup.date().required('This field is required'),
        typeId: Yup.number().required('This field is required'),
        ...(isDayView
          ? {
              startTime: Yup.string().required('This field is required'),
              endTime: Yup.string().required('This field is required'),
            }
          : {}),
      }),
    [isDayView],
  );
  const alert = useNotifilcationDialog();
  const [serverError, setServerError] = useState<string | null>(null);

  const lockMessage = useMemo(() => {
    if (!isAbsenceLockErrorStatus(absenceLock.status)) {
      return null;
    }
    // Waiting matters more here than in the details pane -- there is unsaved
    // work in the fields -- so say who has it, live, rather than repeating
    // whatever the failed acquire happened to return.
    if (absenceEditor) {
      return `${absenceEditor.ownerName} is editing this employee right now.`;
    }
    return absenceLock.message || 'This user is currently locked for editing.';
  }, [absenceEditor, absenceLock.message, absenceLock.status]);

  const isFormEditable = isAbsenceLockHeldStatus(absenceLock.status);

  useEffect(() => {
    if (isAbsenceLockHeldStatus(absenceLock.status) || !selectedSlot?.userId) {
      setServerError(null);
    }
  }, [absenceLock.status, selectedSlot?.userId]);

  const onSubmit = async (values: Record<string, unknown>) => {
    setServerError(null);
    const editedAbsenceIds =
      selectedSlot?.editedAbsenceIds ??
      (selectedSlot?.editedAbsenceId ? [selectedSlot.editedAbsenceId] : []);
    const editedAbsences = editedAbsenceIds.length
      ? absences.filter((a) => editedAbsenceIds.includes(a.id))
      : [];
    const editedAbsence = editedAbsences[0] ?? null;
    const isTypeChanged =
      !!editedAbsence && editedAbsence.typeId !== values.typeId;

    const payload = {
      ...values,
      absenceId: selectedSlot?.editedAbsenceId ?? null,
      editedAbsenceIds,
      userId: selectedSlot?.userId,
      dateFrom: values.dateFrom,
      dateTo: !values.dateTo ? values.dateFrom : values.dateTo,
    };

    // One change at a time. Moving an absence and retyping it in one go would
    // be two different questions for the approver, and the request can only
    // describe one of them.
    if (isTypeChanged && hasRangeChanged(editedAbsences, payload)) {
      setServerError('Change either the dates or the type, not both at once.');
      return undefined;
    }

    if (!selectedSlot?.selectedIds.length && !isTypeChanged) {
      return book(payload);
    }

    if (isDayView) {
      // Retyping or reshaping the absence being edited is one hourly row
      // being rewritten, and the server answers for it -- writing the change
      // over it where somebody has approved the hours, replacing it where
      // nobody has. Only a selection taking in absences other than that one
      // is beyond what the day view can ask for.
      const others = (selectedSlot?.selectedIds ?? []).filter(
        (id) => !editedAbsenceIds.includes(id),
      );
      if (!others.length) {
        return book(payload);
      }
      setServerError(
        'Selected time overlaps an existing hourly absence for this user.',
      );
      return undefined;
    }

    const overrideIds =
      isTypeChanged && editedAbsenceIds.length > 0
        ? [
            ...new Set([
              ...(selectedSlot?.selectedIds ?? []),
              ...editedAbsenceIds,
            ]),
          ]
        : (selectedSlot?.selectedIds ?? []);

    const start = selectedSlot?.start;
    const end = selectedSlot?.end;
    if (start && end) {
      // Days somebody approved are not overwritten -- the change is proposed
      // over them and goes to whoever approved them -- so only the days that
      // really are about to be replaced are worth confirming.
      const overwrittenIds = overrideIds.filter((id) => {
        const absence = absences.find((item) => item.id === id);
        return (
          !!absence &&
          !requiresChangeRequest(
            absence,
            absenceTypes.find((type) => type.id === absence.typeId),
          )
        );
      });
      if (!overwrittenIds.length) {
        return book(payload);
      }

      const overrideDetails = getOverrideAbsenceDetails(
        overwrittenIds,
        absences,
        absenceTypes,
      );
      const { action } = await alert.open({
        title: 'Warning',
        children: (
          <Stack spacing={1} alignItems="center">
            <Typography variant="body1">
              You are about to override existing absence events:
            </Typography>
            <table>
              <tbody>
                {overrideDetails.map(
                  ({ key, typeName, from, to, startTime, endTime }) => (
                    <tr key={key}>
                      <td style={{ textAlign: 'left', paddingRight: 16 }}>
                        <Typography variant="h4">{typeName}:</Typography>
                      </td>
                      <td style={{ textAlign: 'left' }}>
                        <Typography variant="h4">
                          {formatDateRange(from, to)}
                          {startTime && endTime
                            ? `, ${startTime} - ${endTime}`
                            : ''}
                        </Typography>
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </Stack>
        ),
        slotProps: {
          confirmButton: { label: 'OK' },
          cancelButton: { label: 'Cancel' },
        },
      });
      if (action === NOTIFICATION_DIALOG_ACTIONS.CONFIRM) {
        return book(payload);
      }
      return undefined;
    }

    return book(payload);
  };

  return (
    <Form
      saveShortcut={isFormEditable && !withdrawScope}
      schema={schema}
      defaultValues={getDefaultValues(selectedSlotData)}
      onError={(error, info) => {
        if (!info.isInvalid) {
          setServerError(getServerErrorMessage(error));
        }
      }}
      onSubmit={(values) => onSubmit(values)}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      {(lockMessage || serverError) && (
        <FormHeader
          tone={lockMessage ? 'warning' : 'error'}
          message={(lockMessage || serverError) as string}
        />
      )}
      <FormFieldset
        disabled={!isFormEditable}
        disableUserField={absenceLock.status === ABSENCE_LOCK_STATUS.ACQUIRING}
        withdrawScope={withdrawScope}
      />
      <ScheduleFormActions
        disabled={!isFormEditable}
        withdrawing={!!withdrawScope}
      />
    </Form>
  );
}
